import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
} from '@mui/material';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ViewListIcon from '@mui/icons-material/ViewList';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CampaignIcon from '@mui/icons-material/Campaign';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import {
  Client,
  ClientTag,
  fetchClients,
  fetchTags,
  fetchPipelineCounts,
  updateClientStage,
  parseHypingProjects,
  STAGES,
  STAGE_LABELS,
  STAGE_COLORS,
  Stage,
} from '../services/api';
import { useMobileMask } from '../context/MobileMaskContext';

// How many cards to fetch per stage per page. The board never loads a whole
// stage at once, so it stays responsive with 20k+ clients.
const PER_PAGE = 20;

// Per-stage paged state. `total` comes from the cheap counts endpoint; `clients`
// grows as the column lazy-loads more pages.
interface StageData {
  clients: Client[];
  total: number;
  loadingMore: boolean;
}

const emptyStageData = (): StageData => ({ clients: [], total: 0, loadingMore: false });

// ─── Touch DnD helpers ───
interface TouchDragState {
  client: Client;
  startX: number;
  startY: number;
  ghostEl: HTMLDivElement | null;
  moved: boolean;
}

export default function Pipeline({ adminMode = false }: { adminMode?: boolean }) {
  const navigate = useNavigate();
  const { formatMobile } = useMobileMask();
  const isMobile = useMediaQuery('(max-width:768px)');
  const [stageData, setStageData] = useState<Record<string, StageData>>(
    () => Object.fromEntries(STAGES.map((s) => [s, emptyStageData()])),
  );
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [search, setSearch] = useState('');
  const [availableTags, setAvailableTags] = useState<ClientTag[]>([]);
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [draggedClient, setDraggedClient] = useState<Client | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  // Touch drag state
  const touchDrag = useRef<TouchDragState | null>(null);
  const dropZonesRef = useRef<Map<string, HTMLElement>>(new Map());
  // Guards against firing multiple "load more" requests for the same column.
  const loadingMoreRef = useRef<Set<string>>(new Set());

  // ─── Load counts + first page of every column (on mount + when search/tags change) ───
  const reload = useCallback(async (searchTerm: string, tagIds: string[]) => {
    setLoading(true);
    try {
      const term = searchTerm.trim() || undefined;
      const tags = tagIds.length > 0 ? tagIds : undefined;
      const [countsRes, ...pages] = await Promise.all([
        fetchPipelineCounts(term, adminMode),
        ...STAGES.map((stage) => fetchClients({ stage, search: term, tags, adminMode, limit: PER_PAGE, offset: 0 })),
      ]);
      const next: Record<string, StageData> = {};
      STAGES.forEach((stage, i) => {
        next[stage] = {
          clients: pages[i].clients,
          // The counts endpoint isn't tag-aware, so when a tag filter is active
          // use the (tag-filtered) page total instead.
          total: tags ? pages[i].total : (countsRes.counts[stage] ?? pages[i].total),
          loadingMore: false,
        };
      });
      setStageData(next);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [adminMode]);

  // Debounced reload on search / tag change; immediate on mount.
  useEffect(() => {
    const t = setTimeout(() => reload(search, filterTags), 300);
    return () => clearTimeout(t);
  }, [search, filterTags, reload]);

  // Populate the tag list for the tag filter dropdown.
  useEffect(() => {
    fetchTags().then(setAvailableTags).catch(() => setAvailableTags([]));
  }, []);
  const tagById = React.useMemo(() => new Map(availableTags.map((t) => [t.Id, t])), [availableTags]);

  // ─── Load the next page for one column ───
  const loadMore = useCallback(async (stage: string) => {
    if (loadingMoreRef.current.has(stage)) return;
    const current = stageData[stage];
    if (!current || current.clients.length >= current.total) return;
    loadingMoreRef.current.add(stage);
    setStageData((prev) => ({ ...prev, [stage]: { ...prev[stage], loadingMore: true } }));
    try {
      const term = search.trim() || undefined;
      const tags = filterTags.length > 0 ? filterTags : undefined;
      const res = await fetchClients({ stage, search: term, tags, adminMode, limit: PER_PAGE, offset: current.clients.length });
      setStageData((prev) => {
        const existing = prev[stage];
        const seen = new Set(existing.clients.map((c) => c.Id));
        const merged = [...existing.clients, ...res.clients.filter((c) => !seen.has(c.Id))];
        return { ...prev, [stage]: { ...existing, clients: merged, total: res.total, loadingMore: false } };
      });
    } catch (e) {
      console.error(e);
      setStageData((prev) => ({ ...prev, [stage]: { ...prev[stage], loadingMore: false } }));
    } finally {
      loadingMoreRef.current.delete(stage);
    }
  }, [stageData, search, filterTags, adminMode]);

  // Register drop zone refs
  const registerDropZone = useCallback((stage: string, el: HTMLElement | null) => {
    if (el) {
      dropZonesRef.current.set(stage, el);
    } else {
      dropZonesRef.current.delete(stage);
    }
  }, []);

  // Find which drop zone a point is over (strip view prefix to return raw stage)
  const findDropZoneAtPoint = useCallback((x: number, y: number): string | null => {
    const entries = Array.from(dropZonesRef.current.entries());
    for (let i = 0; i < entries.length; i++) {
      const [key, el] = entries[i];
      const rect = el.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        return key.replace(/^(kanban|list)-/, '');
      }
    }
    return null;
  }, []);

  // ─── Move client to a new stage (optimistic; adjusts both lists + counts) ───
  const moveClient = useCallback(async (client: Client, targetStage: string) => {
    if (client.Stage === targetStage) return;
    const oldStage = client.Stage;

    setStageData((prev) => {
      const next = { ...prev };
      const from = prev[oldStage];
      const to = prev[targetStage];
      if (from) {
        next[oldStage] = {
          ...from,
          clients: from.clients.filter((c) => c.Id !== client.Id),
          total: Math.max(0, from.total - 1),
        };
      }
      if (to) {
        next[targetStage] = {
          ...to,
          clients: [{ ...client, Stage: targetStage }, ...to.clients.filter((c) => c.Id !== client.Id)],
          total: to.total + 1,
        };
      }
      return next;
    });

    try {
      await updateClientStage(client.Id, targetStage);
    } catch (e) {
      console.error(e);
      reload(search, filterTags);
    }
  }, [reload, search, filterTags]);

  // ─── HTML5 DnD (desktop) ───
  const handleDragStart = (e: React.DragEvent, client: Client) => {
    setDraggedClient(client);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', client.Id);
  };

  const handleDragOver = (e: React.DragEvent, stage: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stage);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e: React.DragEvent, targetStage: string) => {
    e.preventDefault();
    setDragOverStage(null);
    if (!draggedClient) return;
    await moveClient(draggedClient, targetStage);
    setDraggedClient(null);
  };

  // ─── Touch DnD (mobile) ───
  const handleTouchStart = useCallback((e: React.TouchEvent, client: Client) => {
    const touch = e.touches[0];
    touchDrag.current = {
      client,
      startX: touch.clientX,
      startY: touch.clientY,
      ghostEl: null,
      moved: false,
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchDrag.current) return;
    const touch = e.touches[0];
    const dx = Math.abs(touch.clientX - touchDrag.current.startX);
    const dy = Math.abs(touch.clientY - touchDrag.current.startY);

    // Only start dragging after 10px movement
    if (!touchDrag.current.moved && dx < 10 && dy < 10) return;
    touchDrag.current.moved = true;

    // Prevent scrolling while dragging
    e.preventDefault();

    // Create or move ghost element
    if (!touchDrag.current.ghostEl) {
      const ghost = document.createElement('div');
      ghost.style.cssText = `
        position: fixed; z-index: 9999; pointer-events: none;
        padding: 8px 16px; border-radius: 8px;
        background: #ed8936; color: white; font-weight: 600;
        font-size: 14px; box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        white-space: nowrap; opacity: 0.95;
      `;
      ghost.textContent = touchDrag.current.client.Name;
      document.body.appendChild(ghost);
      touchDrag.current.ghostEl = ghost;
      setDraggedClient(touchDrag.current.client);
    }

    touchDrag.current.ghostEl.style.left = `${touch.clientX - 40}px`;
    touchDrag.current.ghostEl.style.top = `${touch.clientY - 20}px`;

    // Highlight target drop zone
    const targetStage = findDropZoneAtPoint(touch.clientX, touch.clientY);
    setDragOverStage(targetStage);
  }, [findDropZoneAtPoint]);

  const handleTouchEnd = useCallback(async () => {
    if (!touchDrag.current) return;

    // Clean up ghost
    if (touchDrag.current.ghostEl) {
      document.body.removeChild(touchDrag.current.ghostEl);
    }

    // If we actually moved and have a target
    if (touchDrag.current.moved && dragOverStage) {
      await moveClient(touchDrag.current.client, dragOverStage);
    }

    setDraggedClient(null);
    setDragOverStage(null);
    touchDrag.current = null;
  }, [dragOverStage, moveClient]);

  // Fire load-more when a column is scrolled near its bottom.
  const handleColumnScroll = useCallback((stage: string) => (e: React.UIEvent<HTMLElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      loadMore(stage);
    }
  }, [loadMore]);

  // ─── Shared card renderer ───
  const renderKanbanCard = (client: Client, stage: Stage) => {
    const hypingNames = parseHypingProjects(client).map((p) => p.name);
    return (
    <Card
      key={client.Id}
      draggable
      onDragStart={(e) => handleDragStart(e, client)}
      onTouchStart={(e) => handleTouchStart(e, client)}
      onTouchMove={(e) => handleTouchMove(e)}
      onTouchEnd={handleTouchEnd}
      onClick={() => {
        if (touchDrag.current?.moved) return; // Don't navigate if we just dragged
        navigate(`/super-connector/clients/${client.Id}`);
      }}
      sx={{
        bgcolor: 'var(--color-surface)',
        mb: 1,
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.15)' },
        borderLeft: `3px solid ${STAGE_COLORS[stage]}`,
        opacity: draggedClient?.Id === client.Id ? 0.4 : 1,
        transition: 'opacity 0.15s',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 0.5, mb: hypingNames.length > 0 ? 0.25 : 0.5 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ color: 'var(--color-text-strong)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {client.Name}
          </Typography>
          {client.Type && (
            <Chip
              label={client.Type === 'buyer' ? 'B' : 'S'}
              size="small"
              sx={{
                height: 18, fontSize: '0.6rem', fontWeight: 700, minWidth: 22, flexShrink: 0,
                bgcolor: client.Type === 'buyer' ? 'rgba(59,130,246,0.18)' : 'rgba(34,197,94,0.18)',
                color: client.Type === 'buyer' ? '#60a5fa' : '#4ade80',
              }}
            />
          )}
        </Box>
        {client.CompanyName && (
          <Typography
            variant="caption"
            sx={{ color: 'var(--color-text-subtle)', fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}
          >
            {client.CompanyName}
          </Typography>
        )}
        {hypingNames.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            <CampaignIcon sx={{ fontSize: 14, color: '#ed8936', flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{ color: 'var(--color-text-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {hypingNames.join(', ')}
            </Typography>
          </Box>
        )}
        {(client.Tags || []).length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
            {(client.Tags || []).map((t) => (
              <Chip
                key={t.Id}
                label={t.Name}
                size="small"
                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936' }}
              />
            ))}
          </Box>
        )}
        {client.Budget && (
          <Typography variant="caption" sx={{ color: '#ed8936', fontWeight: 600, display: 'block' }}>
            ${Number(client.Budget).toLocaleString()}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', fontSize: '0.65rem' }}>
            {new Date(client.UpdatedAt).toLocaleDateString()}
          </Typography>
          {client.Mobile && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const phone = client.Mobile!.replace(/[\s\-()]/g, '');
                const num = phone.startsWith('+') ? phone.replace('+', '') : phone.startsWith('65') ? phone : `65${phone}`;
                window.open(`https://wa.me/${num}`, '_blank');
              }}
              sx={{ color: '#25D366', p: 0.25 }}
            >
              <WhatsAppIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      </CardContent>
    </Card>
    );
  };

  const renderListCard = (client: Client, stage: Stage) => {
    const hypingNames = parseHypingProjects(client).map((p) => p.name);
    return (
    <Card
      key={client.Id}
      draggable
      onDragStart={(e) => handleDragStart(e, client)}
      onTouchStart={(e) => handleTouchStart(e, client)}
      onTouchMove={(e) => handleTouchMove(e)}
      onTouchEnd={handleTouchEnd}
      onClick={() => {
        if (touchDrag.current?.moved) return;
        navigate(`/super-connector/clients/${client.Id}`);
      }}
      sx={{
        bgcolor: 'var(--color-bg)',
        boxShadow: 'none',
        mb: 0.75,
        '&:last-of-type': { mb: 0 },
        cursor: 'grab',
        borderRadius: 1.5,
        border: '1px solid var(--color-border)',
        '&:active': { cursor: 'grabbing' },
        '&:hover': { borderColor: STAGE_COLORS[stage], bgcolor: 'rgba(var(--color-text-rgb), 0.04)' },
        opacity: draggedClient?.Id === client.Id ? 0.4 : 1,
        transition: 'opacity 0.15s, border-color 0.15s',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.25, px: 2, '&:last-child': { pb: 1.25 } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: 'var(--color-text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {client.Name}
            </Typography>
            {client.Type && (
              <Chip
                label={client.Type === 'buyer' ? 'Buyer' : 'Seller'}
                size="small"
                sx={{
                  height: 18, fontSize: '0.6rem', fontWeight: 700, flexShrink: 0,
                  bgcolor: client.Type === 'buyer' ? 'rgba(59,130,246,0.18)' : 'rgba(34,197,94,0.18)',
                  color: client.Type === 'buyer' ? '#60a5fa' : '#4ade80',
                }}
              />
            )}
          </Box>
          {client.CompanyName && (
            <Typography
              variant="caption"
              sx={{ color: 'var(--color-text-subtle)', fontWeight: 600, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mt: 0.25 }}
            >
              {client.CompanyName}
            </Typography>
          )}
          {hypingNames.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
              <CampaignIcon sx={{ fontSize: 13, color: '#ed8936', flexShrink: 0 }} />
              <Typography
                variant="caption"
                sx={{ color: 'var(--color-text-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {hypingNames.join(', ')}
              </Typography>
            </Box>
          )}
          {(client.Tags || []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {(client.Tags || []).map((t) => (
                <Chip
                  key={t.Id}
                  label={t.Name}
                  size="small"
                  sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936' }}
                />
              ))}
            </Box>
          )}
        </Box>
        {client.Budget && (
          <Typography variant="caption" sx={{ color: '#ed8936', fontWeight: 600, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
            ${Number(client.Budget).toLocaleString()}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
          {formatMobile(client.Mobile)}
        </Typography>
        {client.Mobile && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const phone = client.Mobile!.replace(/[\s\-()]/g, '');
              const num = phone.startsWith('+') ? phone.replace('+', '') : phone.startsWith('65') ? phone : `65${phone}`;
              window.open(`https://wa.me/${num}`, '_blank');
            }}
            sx={{ color: '#25D366', p: 0.25, flexShrink: 0 }}
          >
            <WhatsAppIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </CardContent>
    </Card>
    );
  };

  // Drop zone styling helper
  const dropZoneSx = (stage: string) => ({
    transition: 'all 0.2s ease',
    ...(dragOverStage === stage && draggedClient?.Stage !== stage
      ? {
          bgcolor: 'rgba(237, 137, 54, 0.08)',
          outline: '2px dashed #ed8936',
          outlineOffset: -2,
        }
      : {}),
  });

  const searchField = (
    <TextField
      size="small"
      placeholder="Search by name, phone or company..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      sx={{ flex: 1, minWidth: 200, maxWidth: 360 }}
      InputProps={{
        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'var(--color-text-faint)' }} /></InputAdornment>,
      }}
    />
  );

  const tagFilter = availableTags.length > 0 ? (
    <Select
      size="small"
      multiple
      value={filterTags}
      onChange={(e) => setFilterTags(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
      displayEmpty
      sx={{ minWidth: 140 }}
      renderValue={(selected) =>
        selected.length === 0 ? (
          'All Tags'
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((id) => {
              const t = tagById.get(id);
              if (!t) return null;
              return (
                <Chip
                  key={id}
                  label={t.Name}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936' }}
                />
              );
            })}
          </Box>
        )
      }
    >
      {availableTags.map((t) => (
        <MenuItem key={t.Id} value={t.Id}>
          <Checkbox
            size="small"
            checked={filterTags.includes(t.Id)}
            sx={{ p: 0.5, mr: 1, '&.Mui-checked': { color: t.Color || '#ed8936' } }}
          />
          <Chip
            label={t.Name}
            size="small"
            sx={{ bgcolor: `${t.Color || '#ed8936'}22`, color: t.Color || '#ed8936', fontWeight: 700 }}
          />
        </MenuItem>
      ))}
    </Select>
  ) : null;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={700}>Sales Pipeline</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1, justifyContent: 'flex-end' }}>
          {searchField}
          {tagFilter}
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={(_, v) => v && setView(v)}
            size="small"
          >
            <ToggleButton value="kanban" sx={{ textTransform: 'none' }}>
              <ViewKanbanIcon fontSize="small" sx={{ mr: 0.5 }} /> Kanban
            </ToggleButton>
            <ToggleButton value="list" sx={{ textTransform: 'none' }}>
              <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} /> List
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : view === 'kanban' ? (
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            alignItems: 'stretch',
            pb: 2,
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: 'repeat(3, minmax(0, 1fr))',
              md: `repeat(${STAGES.length}, minmax(0, 1fr))`,
            },
          }}
        >
          {STAGES.map((stage) => {
            const data = stageData[stage] || emptyStageData();
            const clients = data.clients;
            const stageValue = clients.reduce((sum, c) => sum + (Number(c.Budget) || 0), 0);
            const hasMore = clients.length < data.total;
            return (
              <Box
                key={stage}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minWidth: 0,
                  bgcolor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderTop: `3px solid ${STAGE_COLORS[stage]}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                {/* Column Header */}
                <Box sx={{ px: 1.5, pt: 1, pb: stageValue > 0 ? 0.25 : 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {STAGE_LABELS[stage]}
                    </Typography>
                    <Chip
                      label={data.total}
                      size="small"
                      sx={{ height: 20, minWidth: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: STAGE_COLORS[stage], color: '#fff' }}
                    />
                  </Box>
                  {stageValue > 0 && (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                      ${stageValue.toLocaleString()}{hasMore ? '+' : ''}
                    </Typography>
                  )}
                </Box>

                {/* Drop zone — bounded height + scroll-driven lazy loading */}
                <Box
                  ref={(el: HTMLElement | null) => registerDropZone(`kanban-${stage}`, el)}
                  onDragOver={(e) => handleDragOver(e, stage)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage)}
                  onScroll={handleColumnScroll(stage)}
                  sx={{
                    flex: 1,
                    bgcolor: 'var(--color-bg)',
                    p: 1,
                    overflowY: 'auto',
                    minHeight: 140,
                    maxHeight: { xs: 420, md: 'calc(100vh - 230px)' },
                    ...dropZoneSx(stage),
                  }}
                >
                  {clients.map((client) => renderKanbanCard(client, stage))}
                  {clients.length === 0 && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      height: '100%', minHeight: 100,
                      border: '1px dashed var(--color-border)', borderRadius: 1,
                    }}>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                        Drop here
                      </Typography>
                    </Box>
                  )}
                  {hasMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                      {data.loadingMore ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Button size="small" onClick={() => loadMore(stage)} sx={{ textTransform: 'none', fontSize: '0.7rem' }}>
                          Load more ({data.total - clients.length})
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : (
        /* List View — each stage is a distinct grouped card */
        <Box>
          {STAGES.map((stage) => {
            const data = stageData[stage] || emptyStageData();
            const clients = data.clients;
            const stageValue = clients.reduce((sum, c) => sum + (Number(c.Budget) || 0), 0);
            const hasMore = clients.length < data.total;
            return (
              <Box
                key={stage}
                ref={(el: HTMLElement | null) => registerDropZone(`list-${stage}`, el)}
                onDragOver={(e) => handleDragOver(e, stage)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, stage)}
                sx={{
                  mb: 2,
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid var(--color-border)',
                  borderLeft: `4px solid ${STAGE_COLORS[stage]}`,
                  bgcolor: 'var(--color-surface)',
                  ...dropZoneSx(stage),
                }}
              >
                {/* Stage header — tinted with the stage colour so it reads as a band */}
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25,
                  bgcolor: `${STAGE_COLORS[stage]}22`,
                  borderBottom: clients.length > 0 ? '1px solid var(--color-border)' : 'none',
                }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'var(--color-text-strong)' }}>
                    {STAGE_LABELS[stage]}
                  </Typography>
                  <Chip
                    label={data.total}
                    size="small"
                    sx={{ height: 20, minWidth: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: STAGE_COLORS[stage], color: '#fff' }}
                  />
                  <Box sx={{ flex: 1 }} />
                  {stageValue > 0 && (
                    <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)', fontWeight: 600 }}>
                      ${stageValue.toLocaleString()}{hasMore ? '+' : ''}
                    </Typography>
                  )}
                </Box>

                {/* Body */}
                <Box sx={{ p: 1 }}>
                  {clients.map((client) => renderListCard(client, stage))}
                  {clients.length === 0 && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      py: 1.5,
                      border: '1px dashed var(--color-border)',
                      borderRadius: 1.5,
                    }}>
                      <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                        Drop clients here
                      </Typography>
                    </Box>
                  )}
                  {hasMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                      {data.loadingMore ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Button size="small" onClick={() => loadMore(stage)} sx={{ textTransform: 'none' }}>
                          Load more ({data.total - clients.length})
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
